import React from 'react'
// libs
import { useStore } from 'react-redux'
import { getFormValues } from 'redux-form'
import { useMutation, useQuery } from '@apollo/client/react'
// hooks
import { useApolloClient, useDefaultObjectSecurity, useModalData } from 'hooks'
// graphql
import { PLAN_KPI_DETAILS_QUERY } from 'graphql/planKPIs/queries'
import { PLAN_KPI_UPDATE_MUTATION } from 'graphql/planKPIs/mutations'
// constants
import { Modals, ENTITIES, KPI_RESULT_TYPE_VALUES, Forms } from 'constants/index'
// types
import { IPlanKpi } from 'types/graphql'
import { NetworkStatus } from '@apollo/client'
// contexts
import { KPIContext } from 'context'
// lodash
import get from 'lodash/get'
import find from 'lodash/fp/find'
import pipe from 'lodash/fp/pipe'
import size from 'lodash/fp/size'
import filter from 'lodash/fp/filter'
import isEmpty from 'lodash/fp/isEmpty'
// utils
import {
  ifElse,
  getFieldValues,
  getPlanFromEntity,
  getValueFromField,
  getValueFromOption,
  generateScaleLabel,
  getOptionsByEntity,
  getOptionFromFields,
  getValuesFromOptions,
  getFieldsInitialValues,
  generateRecurrenceLabel,
  generateInitialOwnedByValue
} from 'utils'
import { handleError } from 'utils/errors/handlers'
import { runPendingUploads } from 'utils/pendingFileUploads'

interface Props {
  entity?: { is: string } & any
}

const withKPI = (EnhancedComponent: React.FC<any>) => {
  const EnhancedWithKPI: React.FC<Props> = (props) => {
    const { client } = useApolloClient()

    const store = useStore()

    const { modalData } = useModalData({ modalType: Modals.KPI_MINIMIZED_MODAL })

    const kpiId = props.entity?.id || modalData?.id
    const parentPermissions = props.entity?.id ? props.entity.parentPermissions : get(modalData, 'parentPermissions')

    const {
      data: detailsQuery,
      refetch,
      networkStatus,
      loading
    } = useQuery(PLAN_KPI_DETAILS_QUERY, {
      skip: !kpiId,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          ids: kpiId
        }
      }
    })

    const [updateMutation] = useMutation(PLAN_KPI_UPDATE_MUTATION)

    // constants
    const entity: IPlanKpi = get(detailsQuery, 'planKPIs.data[0]')
    const plan = getPlanFromEntity(entity)
    const isLoading = !entity && ((loading && networkStatus !== NetworkStatus.refetch) || (!!kpiId && modalData.isOpen))
    const defaultObjectSecurities = useDefaultObjectSecurity({ objectApiName: 'kpis', planType: plan.planType })

    // methods
    const getInitialValues = async () => {
      const resultTypeScale = get(entity, 'resultTypeScale', [])

      const resultBaselineScale = pipe(
        find(({ id }) => id === entity.resultBaselineScale),
        (result: any) => {
          if (result) {
            return { label: result.label, value: result.id }
          }
        }
      )(resultTypeScale)

      const resultTypeScaleText = pipe(
        filter(({ active }) => active),
        (result) => ifElse(size, () => generateScaleLabel(result), 'Set Indicator Scale')(result)
      )(resultTypeScale)

      const fieldInitialValues = await getFieldsInitialValues({
        fieldValues: get(entity, 'fieldValueMap', []),
        fields: get(entity, 'fields', []),
        client
      })

      return {
        ...fieldInitialValues,
        description: entity.description,
        ownedByEmployeeOrPosition: generateInitialOwnedByValue(entity),
        plan: plan ? { value: plan.id, label: plan.name } : null,
        recurrenceText: isEmpty(entity.recurrence)
          ? 'Set Indicator Recurrence'
          : generateRecurrenceLabel(entity.recurrence),
        resultBaseline: entity.resultType === KPI_RESULT_TYPE_VALUES.VALUE ? entity.resultBaseline : null,
        resultBaselineScale: entity.resultType === KPI_RESULT_TYPE_VALUES.SCALE ? resultBaselineScale : null,
        resultTargetText: isEmpty(entity.resultTarget) ? 'Set Result Targets' : 'Modify Result Targets',
        resultInputType:
          get(fieldInitialValues, 'resultInputType') ||
          getOptionFromFields(entity.fields, 'resultInputType', entity.resultType),
        inputMethod:
          get(fieldInitialValues, 'inputMethod') ||
          getOptionFromFields(entity.fields, 'inputMethod', entity.inputMethod),
        resultTypeScaleText
      }
    }

    const update = async ({ variables }) => {
      await updateMutation({ variables })
    }

    const handleDetailsSubmit = async (formData) => {
      try {
        await runPendingUploads(Forms.KPI_DETAILS_FORM)
        const data = getFormValues(Forms.KPI_DETAILS_FORM)(store.getState()) || formData

        await update({
          variables: {
            id: entity.id,
            input: {
              description: data.description,
              fieldValues: getFieldValues(entity.fields, data, defaultObjectSecurities),
              memberPositionIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.POSITION)),
              memberUserIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.USER)),
              ownedById: getValueFromOption(data.ownedByEmployeeOrPosition),
              recurrence: data.recurrence,
              resultBaseline: data.resultBaseline,
              resultBaselineScale: getValueFromOption(data.resultBaselineScale),
              resultTarget: data.resultTarget,
              resultType: getValueFromField(data.resultInputType, entity.fields, 'resultInputType'),
              resultTypeScale: data.resultTypeScale
            }
          }
        })
      } catch (errors) {
        handleError(errors, 'withKPI.handleDetailsSubmit')
      }
    }

    if (!isLoading && !entity && !modalData.isOpen) {
      return null
    }

    const value = {
      plan,
      update,
      refetch,
      isLoading,
      getInitialValues,
      handleDetailsSubmit,
      entity: { ...entity, parentPermissions }
    }

    return (
      <KPIContext.Provider value={value}>
        <EnhancedComponent {...props} />
      </KPIContext.Provider>
    )
  }

  return EnhancedWithKPI
}

export default withKPI
